import {
    AutoLayoutHelpers,
    createBakedPseudoNode,
    getStackupLayersAndViaConfigs,
    DocumentService,
} from "@buildwithflux/core";
import {
    AliffBoard,
    AliffBoundaryUserData,
    AliffBoundaryUserDataSchema,
    AliffVia,
    AliffWire,
    AutoLayoutIterationClientData,
    AutoLayoutIterationNode,
    AutoLayoutIterationNodeWithoutUid,
    BakedRulesFor,
    IPcbBoardLayerCopper,
    LayerOrientation,
    PcbBakedNode,
    PcbBoardLayer,
    PcbNodeTypes,
    TraceShapeType,
    createRouteSegmentNode,
    getLayerIdentifier,
    getOrderedCopperLayers,
} from "@buildwithflux/models";
import {Logger} from "@buildwithflux/shared";
import {pick} from "lodash";
import {Vector3} from "three";
import {ZodError} from "zod";

import type {DocumentStore} from "modules/document/state/document";
import {getAllBakedNodes, getBakedNode, getTypedBakedNode} from "modules/stores/pcb/utils";

import {AutoLayoutIterationFactory} from "./AutoLayoutIterationFactory";

type TraceShapeBakedRules = Partial<
    Pick<BakedRulesFor<PcbNodeTypes.routeSegment>, "traceShape" | "traceShapeStart" | "traceShapeEnd">
>;

export class DeepPcbAutoLayoutIterationFactory implements AutoLayoutIterationFactory {
    constructor(
        private readonly documentService: DocumentService,
        private readonly logger: Logger,
        private readonly documentStore: DocumentStore,
    ) {}

    /**
     * @inheritdoc
     */
    public generateIterationClientData(
        iteration: AutoLayoutIterationClientData,
        board: AliffBoard,
    ): AutoLayoutIterationClientData {
        const newNodes = this.generateIterationNodes(board);

        return {
            ...iteration,
            nodes: newNodes,
            loaded: true,
        };
    }

    private parseBoardBoundaryUserData(board: AliffBoard): AliffBoundaryUserData | undefined {
        if (!board.boundary.userData) {
            return undefined;
        }

        try {
            const parsed = JSON.parse(board.boundary.userData);
            return AliffBoundaryUserDataSchema.parse(parsed);
        } catch (error) {
            if (error instanceof SyntaxError) {
                this.logger.error("Failed to parse board.boundary.userData as JSON", {
                    error: error.message,
                    userData: board.boundary.userData,
                });
            } else if (error instanceof ZodError) {
                this.logger.error("Board.boundary.userData failed Zod schema validation", {
                    error: error.message,
                    userData: board.boundary.userData,
                    zodErrors: error.errors,
                });
            } else {
                this.logger.error("Unexpected error parsing board.boundary.userData", {
                    error: error,
                    userData: board.boundary.userData,
                });
            }
            return undefined;
        }
    }

    private generateIterationNodes(board: AliffBoard) {
        const newNodes: Record<string, AutoLayoutIterationNode> = {};

        const boundaryUserData = this.parseBoardBoundaryUserData(board);

        const getNetNodeUid = (netId: string): string | undefined => {
            // If the board boundary does not have a 'userData' property with 'objectIdToNodeUidMap' it means
            // the board was not exported with the new netId format (`${net.name}_${hash(net.uid)}`).
            // In that case, we can use the `netUid` directly from the node data.
            const objectIdToNodeUidMap = boundaryUserData?.objectIdToNodeUidMap;
            if (objectIdToNodeUidMap) {
                return objectIdToNodeUidMap[netId];
            }
            return netId;
        };

        let copperLayers: IPcbBoardLayerCopper[];
        const getCopperLayers = (anyBakedNodeUid: string) => {
            if (copperLayers) {
                return copperLayers;
            }

            // If the layoutUid is defined, it's always better to use that instead of
            // inferring the layout uid from some child node.
            // TODO: Once some time has passed and we're assured that all boards have the layoutUid defined,
            //       we can remove the fallback logic below.
            const layoutNodeUid = boundaryUserData?.layoutUid;
            if (layoutNodeUid) {
                const layoutNode = getTypedBakedNode(this.documentService, layoutNodeUid, PcbNodeTypes.layout);
                if (layoutNode) {
                    copperLayers = getOrderedCopperLayers(layoutNode.bakedRules.stackup);
                    return copperLayers;
                }
            }

            const bakedNode = getBakedNode(this.documentService, anyBakedNodeUid);
            if (!bakedNode) {
                this.logger.error("Baked node not found", {anyBakedNodeUid});
                return [];
            }

            copperLayers = getStackupLayersAndViaConfigs(
                bakedNode.bakedRules,
                getAllBakedNodes(this.documentService),
            ).copperLayers;
            return copperLayers;
        };

        const getNetTraceShapes_cache: Record<string, TraceShapeBakedRules> = {};

        for (const viaOrWireNode of [...board.vias, ...board.wires]) {
            // Skip protected nodes (nodes that are user-generated)
            if (viaOrWireNode.protected) {
                continue;
            }

            const netUid = getNetNodeUid(viaOrWireNode.netId);
            if (!netUid) {
                continue;
            }

            // Ensure that downstream consumers have the correct netUid (pointing to the net node)
            viaOrWireNode.netId = netUid;

            const nodeData = this.getAutoLayoutNodeData(
                viaOrWireNode,
                getCopperLayers(netUid),
                getNetTraceShapes_cache,
            );
            if (!nodeData) {
                continue;
            }

            const uid = AutoLayoutHelpers.createNodeObjectUid(nodeData);
            newNodes[uid] = {
                ...nodeData,
                uid,
            };
        }

        return newNodes;
    }

    private getAutoLayoutNodeData(
        aliffNode: AliffVia | AliffWire,
        copperLayers: IPcbBoardLayerCopper<PcbBoardLayer>[],
        getNetTraceShapes_cache: Record<string, TraceShapeBakedRules>,
    ): AutoLayoutIterationNodeWithoutUid | null {
        //
        // Via node
        //
        if ("padstack" in aliffNode) {
            return {
                netUid: aliffNode.netId,
                type: PcbNodeTypes.smartVia,
                position: AutoLayoutHelpers.pointToXY(aliffNode.position),
                viaConfigUid: aliffNode.padstack,
            };
        }

        //
        // Trace node
        //
        const layer = copperLayers[aliffNode.layer];
        if (!layer) return null;

        const startPosition = AutoLayoutHelpers.pointToXY(aliffNode.start);
        const endPosition = AutoLayoutHelpers.pointToXY(aliffNode.end);
        const width = AutoLayoutHelpers.nmToM(aliffNode.width);

        // Force straight traces if we've ascertained the trace will be curved by virtue of being in that net
        const traceShapesForNet = this.getNetTraceShapes(aliffNode.netId, getNetTraceShapes_cache);

        return {
            netUid: aliffNode.netId,
            type: PcbNodeTypes.routeSegment,
            layerId: getLayerIdentifier(layer),
            startPosition,
            endPosition,
            width,
            traceShape: traceShapesForNet.traceShape === TraceShapeType.curve ? TraceShapeType.straight : undefined,
            traceShapeStart:
                traceShapesForNet.traceShapeStart === TraceShapeType.curve ? TraceShapeType.straight : undefined,
            traceShapeEnd:
                traceShapesForNet.traceShapeEnd === TraceShapeType.curve ? TraceShapeType.straight : undefined,
        };
    }

    private getNetTraceShapes(
        netUid: string,
        getNetTraceShapes_cache: Record<string, TraceShapeBakedRules>,
    ): TraceShapeBakedRules {
        const cachedValue = getNetTraceShapes_cache[netUid];
        if (cachedValue !== undefined) {
            return cachedValue;
        }

        const pseudoTraceNode = this.createPseudoTraceNodeForNetWithUid(netUid);
        const traceShapes = pick<TraceShapeBakedRules>(
            pseudoTraceNode?.bakedRules ?? {},
            "traceShape",
            "traceShapeStart",
            "traceShapeEnd",
        );

        getNetTraceShapes_cache[netUid] = traceShapes;

        return traceShapes;
    }

    private createPseudoTraceNodeForNetWithUid(netUid: string): PcbBakedNode<PcbNodeTypes.routeSegment> | undefined {
        const document = this.documentStore.getState().document;
        if (!document) {
            return undefined;
        }

        const traceNode = createRouteSegmentNode(
            "startId",
            "endId",
            new Vector3(),
            new Vector3(),
            netUid,
            LayerOrientation.top,
        );
        return createBakedPseudoNode(traceNode, document, getAllBakedNodes(this.documentService));
    }
}
